import { Filters, FilterType } from "@/lib/filters";
import { ColumnFiltersState, SortingState, Table } from "@tanstack/react-table";

export const dataTableOnlySelectedViewModes = {
  all: "all",
  selected: "selected",
} as const;

export type DataTableOnlySelectedViewMode =
  (typeof dataTableOnlySelectedViewModes)[keyof typeof dataTableOnlySelectedViewModes];

export interface FilterValue {
  value: string | undefined;
  filter?: FilterType;
}
export interface ExportSettings {
  moduleName: string;
  queryParams?: Record<string, string>;
}

export interface DataTableFeatures {
  /** When set, enables per-cell dirty highlighting. ListContent injects FormTableCellDirtyProvider automatically. Use with useFormBaseline: pass { baseline, recordKey? }. recordKey defaults to "entries". */
  editionHighlight?: { baseline: object; recordKey?: string };
  enableColumnPinning?: boolean;
  enableColumnOrder?: boolean;
  exportSettings?: ExportSettings;
  enableFilters?: boolean;
  enableSorting?: boolean;
  enableColumnVisibility?: boolean;
  initialColumnSizes?: Record<string, number>;
  initialColumnVisibility?: Record<string, boolean>;
  enableRowSelection?: boolean;
  enableOnlySelectedFilter?: boolean;
  onlySelectedViewMode?: DataTableOnlySelectedViewMode;
  enableGrouping?: boolean;
  groupBy?: string[];
  getRowProps?: <T>(row: { original: T }) => Record<string, unknown>;
  enableSelectAll?: boolean;
  enableCopySelection?: boolean;
  /** When true, DefaultList renders a "Show deleted" checkbox in the Settings menu and injects include_deleted=true into fetchList when checked. */
  enableShowDeletedToggle?: boolean;
}

export interface UseDataTableOptions<T> {
  filters: Filters<T>;
  rawFilters: ColumnFiltersState;
  setFilters: (
    filters:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState),
  ) => void;
  sorting: SortingState;
  setSorting: (columnId: string, value: false | "asc" | "desc") => void;
  setFilteredMark: (columnId: string, marked: boolean) => void;
  setFilterValue: (columnId: string, value: FilterValue) => void;
  getFilterValue: (columnId: string) => FilterValue | undefined;
  resetFilters: () => void;
  resetFilterById: (columnId: string) => void;
  resetSorting: () => void;
  isFilteredOrSorted: (columnId?: string) => boolean;
  isFiltered: (columnId?: string) => boolean;
  isSorted: (columnId?: string) => boolean;
  getSortValue: (columnId: string) => "asc" | "desc" | false;
  clearSorting: (columnId: string) => void;
}

export interface DataTableSettings<T> {
  features: DataTableFeatures;
  options: UseDataTableOptions<T>;
  table: Table<T>;
  isResizing?: boolean;
  firstVisibleColumnId?: string;
  rowsCount?: number;
}
